/*
 * False-positive report modal. Ported from openFalsePositiveModal /
 * sendFalsePositiveReport. Reads the active occurrence (selection) or doc
 * suggestion from the store based on reportModalTarget and posts the report.
 */

import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import { getFriendlyRule } from "../../rules/ruleLogic";
import { submitFalsePositiveReport } from "../../api/client";

const useStyles = makeStyles({
  body: { display: "flex", flexDirection: "column", gap: "10px" },
  field: { display: "flex", flexDirection: "column", gap: "2px" },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

export function FalsePositiveModal(): JSX.Element | null {
  const styles = useStyles();
  const { t, lang } = useTranslation();
  const patch = useStore((s) => s.patch);
  const open = useStore((s) => s.reportModalOpen);
  const target = useStore((s) => s.reportModalTarget);
  const rules = useStore((s) => s.rules);
  const catalogSections = useStore((s) => s.catalogSections);
  const occurrences = useStore((s) => s.occurrences);
  const currentOccurrenceIndex = useStore((s) => s.currentOccurrenceIndex);
  const docSuggestions = useStore((s) => s.docSuggestions);
  const currentDocSuggestionIndex = useStore((s) => s.currentDocSuggestionIndex);
  const reviewSelectionSuggestions = useStore((s) => s.reviewSelectionSuggestions);
  const currentReviewSelectionIndex = useStore((s) => s.currentReviewSelectionIndex);

  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  if (!open) return null;

  const change =
    target === "selection"
      ? (() => {
          const occ = occurrences[currentOccurrenceIndex];
          return occ
            ? {
                ruleId: occ.ruleId,
                before: occ.word,
                after: occ.replacement,
                sentenceBefore: occ.sentenceContext,
                sentenceAfter: occ.sentenceContext,
              }
            : null;
        })()
      : target === "review-selection"
        ? reviewSelectionSuggestions[currentReviewSelectionIndex] || null
        : docSuggestions[currentDocSuggestionIndex] || null;

  const close = () => {
    setFeedback("");
    setSuccess(false);
    setError(false);
    setSending(false);
    patch({ reportModalOpen: false, reportModalTarget: null });
  };

  const friendly = change ? getFriendlyRule(change.ruleId, rules, catalogSections, lang) : null;

  const send = async () => {
    if (!change || sending) return;
    setSending(true);
    setSuccess(false);
    setError(false);
    try {
      await submitFalsePositiveReport({
        ruleId: change.ruleId,
        ruleTitle: friendly?.name ?? "",
        ruleDescription: friendly?.desc ?? "",
        beforeText: change.before || "",
        afterText: change.after || "",
        sentenceBefore: change.sentenceBefore || "",
        sentenceAfter: change.sentenceAfter || "",
        userFeedback: feedback.trim(),
      });
      setSuccess(true);
      window.setTimeout(close, 1800);
    } catch {
      setError(true);
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_e, data) => (!data.open ? close() : undefined)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.reportFalsePositiveTitle}</DialogTitle>
          <DialogContent className={styles.body}>
            <Text>{t.reportFalsePositiveDescription}</Text>
            {change ? (
              <>
                <div className={styles.field}>
                  <span className={styles.label}>{t.cardRule}</span>
                  <Text>{change.ruleId}</Text>
                </div>
                <div className={styles.field}>
                  <span className={styles.label}>{t.cardOriginal}</span>
                  <Text>{change.before || ""}</Text>
                </div>
                <div className={styles.field}>
                  <span className={styles.label}>{t.cardSuggested}</span>
                  <Text>{change.after || ""}</Text>
                </div>
              </>
            ) : null}
            <Field label={t.reportFalsePositiveFeedbackLabel}>
              <Textarea
                value={feedback}
                placeholder={t.reportFalsePositiveFeedbackPlaceholder}
                onChange={(_e, data) => setFeedback(data.value)}
              />
            </Field>
            {success ? (
              <MessageBar intent="success">
                <MessageBarBody>{t.reportFalsePositiveSuccess}</MessageBarBody>
              </MessageBar>
            ) : null}
            {error ? (
              <MessageBar intent="error">
                <MessageBarBody>{t.reportFalsePositiveError}</MessageBarBody>
              </MessageBar>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>
              {t.reportFalsePositiveCancel}
            </Button>
            <Button appearance="primary" onClick={send} disabled={sending || !change}>
              {sending ? t.reportFalsePositiveSending : t.reportFalsePositiveSend}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
